import { useEffect, useRef, useState } from "react";
import NavLink from "../components/NavLink";
import Button from "../components/Button";
import Toggle from "../components/Toggle";
import menus from "../models/menus.json";
import options from "../models/options.json";
import Dropdown from "../components/Dropdown";
import IconComponent from "../components/Icon";
import { useCurrency } from "../contexts/currency";
import { useBanner } from "../contexts/banner";

const MENU_LINK_CLASS = "group flex items-start gap-2.5 rounded-lg border border-white/50 bg-white/30 px-3 py-2.5 text-sm text-slate-700 transition shadow-lg backdrop-blur-md backdrop-saturate-150 hover:border-brand-orange/40 hover:text-brand-orange dark:border-slate-700/60 dark:bg-slate-900/30 dark:text-slate-300 dark:hover:border-brand-orange/40 dark:hover:text-brand-orange";
const DESKTOP_MENU_CLASS = "flex items-center rounded-lg px-2 py-6 text-[1.05rem] font-semibold text-slate-700 transition hover:text-brand-orange dark:text-slate-300 dark:hover:text-brand-orange";

const isDropdownMenu = (menu) => !menu.href;

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDesktopMenu, setActiveDesktopMenu] = useState(null);
  const [mobileDropdowns, setMobileDropdowns] = useState( menus.reduce((menus, menu) => ({ ...menus, [menu.id]: false }), {}) );
  const [mobileGroups, setMobileGroups] = useState({});
  const [isScrolled, setIsScrolled] = useState(false);
  const desktopCloseTimeoutRef = useRef(null);
  const { currency, selectedCurrency, setCurrency } = useCurrency();
  const currencyLabel = selectedCurrency?.label || "INR";
  const { showBanner } = useBanner();

  const openDesktopDropdown = (menuId) => {
    if (desktopCloseTimeoutRef.current) {
      clearTimeout(desktopCloseTimeoutRef.current);
      desktopCloseTimeoutRef.current = null;
    }
    setActiveDesktopMenu(menuId);
  };

  const closeDesktopDropdownWithDelay = () => {
    if (desktopCloseTimeoutRef.current) {
      clearTimeout(desktopCloseTimeoutRef.current);
    }
    desktopCloseTimeoutRef.current = setTimeout(() => {
      setActiveDesktopMenu(null);
    }, 220);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => () => {
    if (desktopCloseTimeoutRef.current) {
      clearTimeout(desktopCloseTimeoutRef.current);
    }
  }, []);

  const toggleMobileDropdown = (menuId) => {
    setMobileDropdowns((prev) => ({ ...prev, [menuId]: !prev[menuId] }));
  };

  const toggleMobileGroup = (key) => {
    setMobileGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderMenuContent = (menu, onNavigate, isMobile = false) => {
    if (menu.layout === "grouped") {
      return (
        <div className={isMobile ? "flex flex-col gap-1" : "grid w-full gap-3 md:grid-cols-2"}>
          {menu.groups.map((group) => {
            const groupKey = `${menu.id}-${group.title}`;
            const isGroupOpen = isMobile ? !!mobileGroups[groupKey] : true;

            return (
              <div key={group.title} className="flex flex-col">
                {isMobile ? (
                  <button
                    type="button" onClick={() => toggleMobileGroup(groupKey)}
                    className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-800 transition hover:text-brand-orange dark:text-slate-400 dark:hover:text-brand-orange"
                  >
                    {group.title}
                    <IconComponent
                      name="chevron-down" size="sm"
                      color={`text-slate-500 transition-transform duration-300 ${isGroupOpen ? "rotate-180" : "rotate-0"}`}
                    />
                  </button>
                ) : (
                  <span className="text-[11px] font-semibold uppercase pb-2 tracking-[0.08em] text-slate-800 dark:text-slate-400">
                    {group.title}
                  </span>
                )}

                <div className={isMobile ? `grid transition-all duration-300 ${isGroupOpen ? "grid-rows-[2fr]" : "grid-rows-[0fr]"}` : ""}>
                  <div className={isMobile ? "overflow-hidden" : ""}>
                    <div className={`flex flex-col gap-1.5 ${isMobile ? "px-1 pb-1.5" : ""}`}>
                      {group.items.map((item) => (
                        <NavLink key={item.label} to={item.href} className={MENU_LINK_CLASS} onClick={onNavigate}>
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-100 text-slate-700 transition group-hover:border-brand-orange/40 group-hover:text-brand-orange dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                            <IconComponent name={item.icon} color="hover:text-brand-orange" />
                          </div>
                          <div className="flex min-w-0 flex-col">
                            <span className="text-sm font-semibold text-slate-900 transition group-hover:text-brand-orange dark:text-slate-100 dark:group-hover:text-brand-orange">
                              {item.label}
                            </span>
                            {item.description && (
                              <span className="truncate text-xs text-slate-500 dark:text-slate-400">
                                {item.description}
                              </span>
                            )}
                          </div>
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        {menu.items.map((item) => (
          <NavLink key={item.label} to={item.href} className={MENU_LINK_CLASS} onClick={onNavigate}>
            {item.label}
          </NavLink>
        ))}
      </div>
    );
  };

  return (
    <header className={`fixed z-50 flex w-full justify-center transition-all duration-300 ${showBanner && !isScrolled ? "top-12" : "top-2 sm:top-4"}`}>
      <div className="layout-shell frost-panel flex w-11/12 items-center justify-between rounded-2xl px-3 py-2 shadow-xl md:py-0 sm:px-5">
        <div className="flex items-center gap-3">
          <NavLink to="/" className="flex items-center gap-3">
            <img src="/logo.webp" className="h-8 w-auto sm:h-14" alt="Hostzop Cloud logo" />
          </NavLink>
        </div>

        <div className="hidden flex-1 items-center justify-center gap-4 lg:flex xl:gap-6">
          {menus.map((menu) => {
            if (!isDropdownMenu(menu)) {
              return (
                <NavLink key={menu.id} to={menu.href} className={DESKTOP_MENU_CLASS}>
                  {menu.title}
                </NavLink>
              );
            }

            return (
              <div
                key={menu.id} className="relative flex items-center"
                onMouseEnter={() => openDesktopDropdown(menu.id)} onMouseLeave={closeDesktopDropdownWithDelay}
              >
                <button type="button" onFocus={() => openDesktopDropdown(menu.id)}
                  className={DESKTOP_MENU_CLASS}
                >
                  {menu.title}
                </button>
                <div
                  className={`frost-panel fixed left-1/2 ${showBanner && !isScrolled ? "top-[8.5rem]" : "top-24"} z-20 flex max-h-[calc(100vh-7rem)] w-11/12 -translate-x-1/2 overflow-y-auto rounded-3xl p-4 shadow-2xl transition-all duration-200 md:w-1/2 ${
                    activeDesktopMenu === menu.id ? "visible opacity-100 translate-y-0" : "pointer-events-none invisible opacity-0 -translate-y-2"
                  }`}
                >
                  {renderMenuContent(menu)}
                </div>
              </div>
            );
          })}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <Toggle />

          <Dropdown
            title={currencyLabel} items={ options.currencyOptions } value={currency}
            onSelect={(item) => setCurrency(item.id)}
          />

          <Button title="Get Started" href="https://cloud.hostzop.com" />
        </div>

        <button
          type="button" onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          className="flex h-10 items-center rounded-lg border border-slate-300 px-3 text-sm font-semibold text-slate-700 transition hover:border-brand-orange hover:text-brand-orange dark:border-slate-600 dark:text-slate-300 lg:hidden"
        >
          {isMobileMenuOpen ? <IconComponent name="close" /> : <IconComponent name="bars" />}
        </button>
      </div>

      <div
        className={`layout-shell frost-panel absolute top-[4.25rem] w-11/12 flex-col gap-4 rounded-2xl shadow-lg transition-all duration-300 lg:hidden ${
          isMobileMenuOpen
            ? "flex max-h-[calc(100vh-6rem)] overflow-y-auto p-4 opacity-100 translate-y-0"
            : "pointer-events-none max-h-0 overflow-hidden p-0 opacity-0 -translate-y-3"
        }`}
      >
        <div className="flex flex-col gap-3">
          {menus.map((menu) => {
            if (!isDropdownMenu(menu)) {
              return (
                <NavLink
                  key={menu.id} to={menu.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex w-full items-center justify-between rounded-xl border border-slate-200 p-3 text-left text-sm font-semibold text-slate-900 dark:border-slate-700 dark:text-slate-100"
                >
                  {menu.title}
                </NavLink>
              );
            }

            const isOpen = mobileDropdowns[menu.id];

            return (
              <div
                key={menu.id}
                className="flex flex-col rounded-xl border border-slate-200 dark:border-slate-700"
              >
                <button
                  type="button" onClick={() => toggleMobileDropdown(menu.id)}
                  className="flex w-full items-center justify-between p-3 text-left text-sm font-semibold text-slate-900 dark:text-slate-100"
                >
                  {menu.title}
                  <IconComponent
                    name="chevron-down" size="sm"
                    color={`text-slate-500 transition-transform duration-300 ${isOpen ? "rotate-180" : "rotate-0"}`}
                  />
                </button>

                <div className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                  <div className="overflow-hidden">
                    <div className="flex flex-col gap-2 px-3 pb-3">
                      {renderMenuContent(menu, () => setIsMobileMenuOpen(false), true)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 dark:border-slate-700">
          <Toggle />
          <Dropdown
            title={currencyLabel} items={ options.currencyOptions } value={currency}
            onSelect={(item) => setCurrency(item.id)}
          />
        </div>
        <div className="flex w-full items-center gap-3">
          <Button title="Get Started" href="https://cloud.hostzop.com" className="w-full" />
        </div>
      </div>
    </header>
  );
};

export default Header;